import { Dispatch, SetStateAction } from 'react'

import { MediaActionSearchCondition } from '@/common/api/modules/mediaAction'
import {
  ACTION_SORT_CHOICES,
  ACTION_STATUS_FILTERS,
  ACTION_TYPE_FILTER_CHOICES,
  ActionTabKey,
  SOURCE_FILTER_CHOICES
} from '@/common/app/modules/media/mediaAction'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import SortButton from '@/components/view/atoms/button/SortButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import SearchConditionAreaContent from '@/components/view/layouts/area/SearchConditionAreaContent'
import SortButtonList from '@/components/view/layouts/button/SortButtonList'
import FormRow from '@/components/view/layouts/form/FormRow'
import DisclosureSortRadioSelect from '@/components/view/molecules/disclosure/DisclosureSortRadioSelect'

interface Props {
  condition: MediaActionSearchCondition
  setCondition: Dispatch<SetStateAction<MediaActionSearchCondition>>
  onChangeStatus: (status: ActionTabKey) => void
  counts: Record<string, number>
  selectedIds: number[]
  onClearSelected: () => void
  onApplyOrderToSelected: () => void
}

// 一覧タブ内の状態フィルタ、フィルタ・ソートバー
const MediaActionSearchConditionField = ({
  condition,
  setCondition,
  onChangeStatus,
  counts,
  selectedIds,
  onClearSelected,
  onApplyOrderToSelected
}: Props) => {
  const view = condition.view ?? 'list'
  const status = condition.status ?? 'suggested'

  return (
    <SearchConditionAreaContent>
      <FormRow size={TextBoxSize.FULL}>
        <TextField
          type='search'
          size={TextBoxSize.FULL}
          value={condition.q ?? ''}
          placeholder='アクション名・判定理由で検索する'
          onChange={(e) => setCondition((c) => ({ ...c, page: 1, q: e.target.value || null }))}
        />
      </FormRow>
      {condition.sourceAnalysisId != null && (
        <div className='flex items-center gap-2'>
          <Badge size={10} badgeType={BadgeType.CAUTION}>
            推奨元分析ID: {condition.sourceAnalysisId}
          </Badge>
          <TextButton onClick={() => setCondition((c) => ({ ...c, sourceAnalysisId: null }))}>
            解除
          </TextButton>
        </div>
      )}
      {/* 一覧タブ内の状態フィルタ */}
      {view === 'list' && (
        <>
          <SortButtonList>
            {ACTION_STATUS_FILTERS.map((t, index) => (
              <SortButtonList key={index} separator={index != 0}>
                <SortButton
                  key={t.key}
                  selected={status === t.key}
                  quantity={counts[t.key] ?? 0}
                  noPulldown
                  onClick={() => onChangeStatus(t.key)}
                >
                  {t.label}
                </SortButton>
              </SortButtonList>
            ))}
            <div className='mr-1' />
            <DisclosureSortRadioSelect
              name='actionType'
              currentValue={condition.actionType}
              choices={ACTION_TYPE_FILTER_CHOICES}
              setCondition={setCondition}
            >
              アクション種別
            </DisclosureSortRadioSelect>
            <DisclosureSortRadioSelect
              name='sourceType'
              currentValue={condition.sourceType}
              choices={SOURCE_FILTER_CHOICES}
              setCondition={setCondition}
            >
              推奨元分析
            </DisclosureSortRadioSelect>
            <DisclosureSortRadioSelect
              name='sort'
              currentValue={condition.sort}
              choices={ACTION_SORT_CHOICES}
              setCondition={setCondition}
            >
              並び順
            </DisclosureSortRadioSelect>
          </SortButtonList>
        </>
      )}
      <div className='flex flex-wrap items-center gap-2'>
        {selectedIds.length > 0 && (
          <div className='ml-auto flex items-center gap-2'>
            <Text size={12} color={TextColor.CAUTION}>
              {selectedIds.length} 件選択中
            </Text>
            <Button color={ButtonColor.PRIMARY_BRAND} onClick={onApplyOrderToSelected}>
              選択順に実行順を設定
            </Button>
            <Button color={ButtonColor.SECONDARY} onClick={onClearSelected}>
              選択クリア
            </Button>
          </div>
        )}
      </div>
    </SearchConditionAreaContent>
  )
}

export default MediaActionSearchConditionField
